import { z } from 'zod';

export const CleanupRunScopeSchema = z.enum(['global', 'application']);

export const CleanupRunTriggerSchema = z.enum(['cron', 'cockpit']);

export const CleanupRunStatusSchema = z.enum([
  'running',
  'succeeded',
  'partial',
  'failed',
  'blocked',
  'already_running',
]);

export const CleanupWorkerSchema = z.enum([
  'scanner',
  'updater',
  'cleanup',
  'post_attack_rerunner',
]);

export const CleanupStepStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'skipped',
]);

export const CleanupSkipReasonSchema = z.enum([
  'PAUSED_OR_BLOCKED',
  'LIVE_THREAD',
  'FAILED_POST_ATTACK',
  'CANNOT_RERUN_POST_ATTACK',
  'CANNOT_FAIL_RETEST',
  'SIGNAL_LOOKUP_FAILED',
]);

export const CleanupIssueCodeSchema = z.enum([
  ...CleanupSkipReasonSchema.options,
  'FEATURE_FLAG_DISABLED',
  'THREAD_CAP_HIT',
  'APPLICATION_CLEANUP_FAILED',
  'LOCK_CONTENTION',
  'KILL_SWITCH_BLOCKED',
  'SIGNAL_UNRESOLVED',
]);

export const CleanupSkippedByReasonSchema = z.object({
  PAUSED_OR_BLOCKED: z.int().nonnegative(),
  LIVE_THREAD: z.int().nonnegative(),
  FAILED_POST_ATTACK: z.int().nonnegative(),
  CANNOT_RERUN_POST_ATTACK: z.int().nonnegative(),
  CANNOT_FAIL_RETEST: z.int().nonnegative(),
  SIGNAL_LOOKUP_FAILED: z.int().nonnegative(),
});

export const CleanupRunStatsSchema = z.object({
  threadsScanned: z.int().nonnegative(),
  tracesDeleted: z.int().nonnegative(),
  tracesMarkedCompleted: z.int().nonnegative(),
  postAttackReruns: z.int().nonnegative(),
  retestsFailed: z.int().nonnegative(),
  applicationsProcessed: z.int().nonnegative(),
  applicationsFailed: z.int().nonnegative(),
  tenantsSkippedFeatureFlag: z.int().nonnegative(),
  capHitApplications: z.int().nonnegative(),
  skippedByReason: CleanupSkippedByReasonSchema,
});

export const CleanupRunStepCountsSchema = z.object({
  threadsScanned: z.int().nonnegative(),
  tracesDeleted: z.int().nonnegative(),
  tracesMarkedCompleted: z.int().nonnegative(),
  postAttackReruns: z.int().nonnegative(),
  retestsFailed: z.int().nonnegative(),
  threadsSkipped: z.int().nonnegative(),
});

export const CleanupRunStepSchema = z.object({
  worker: CleanupWorkerSchema,
  status: CleanupStepStatusSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().optional(),
  counts: CleanupRunStepCountsSchema,
});

export const CleanupRunIssueSchema = z.object({
  code: CleanupIssueCodeSchema,
  applicationId: z.string().optional(),
  threadId: z.string().optional(),
  message: z.string().optional(),
});

export const CleanupRunDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  parentRunId: z.string().nullable(),
  scope: CleanupRunScopeSchema,
  applicationId: z.string().nullable(),
  trigger: CleanupRunTriggerSchema,
  triggeredBy: z.string().nullable(),
  status: CleanupRunStatusSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  stats: CleanupRunStatsSchema,
  steps: CleanupRunStepSchema.array(),
  issues: CleanupRunIssueSchema.array(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CleanupRunHealthSchema = z.enum([
  'healthy',
  'attention',
  'critical',
]);

export const CleanupDailyOutcomeSchema = z.object({
  date: z.iso.date(),
  succeeded: z.int().nonnegative(),
  partial: z.int().nonnegative(),
  failed: z.int().nonnegative(),
  blocked: z.int().nonnegative(),
  alreadyRunning: z.int().nonnegative(),
});

export const CleanupSkipReasonBreakdownItemSchema = z.object({
  reason: CleanupSkipReasonSchema,
  count: z.int().nonnegative(),
});

export const StaleNeoTraceCleanupDashboardSchema = z.object({
  summary: z.object({
    runCount: z.int().nonnegative(),
    tracesResolved: z.int().nonnegative(),
    successRate: z.number().min(0).max(100),
    skipRate: z.number().min(0).max(100),
    lastRunAt: z.iso.datetime().nullable(),
  }),
  health: CleanupRunHealthSchema,
  dailyOutcomes: CleanupDailyOutcomeSchema.array(),
  skipReasons: CleanupSkipReasonBreakdownItemSchema.array(),
  attentionIssues: CleanupRunIssueSchema.array(),
});

export const StaleNeoTraceCleanupDateRangeSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine(({ from, to }) => new Date(from) <= new Date(to), {
    error: 'from must be before or equal to to',
  });

export const GetStaleNeoTraceCleanupDashboardQuerySchema =
  StaleNeoTraceCleanupDateRangeSchema;

export const ListStaleNeoTraceCleanupRunsQuerySchema =
  StaleNeoTraceCleanupDateRangeSchema.and(
    z.object({
      scope: CleanupRunScopeSchema.optional(),
      applicationId: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(100).prefault(25),
    }),
  );

export const ListStaleNeoTraceCleanupRunsResponseSchema = z.object({
  items: CleanupRunDtoSchema.array(),
  nextCursor: z.string().nullable(),
});

export const GetStaleNeoTraceCleanupRunParamsSchema = z.object({
  runId: z.string().regex(/^[0-9a-fA-F]{24}$/),
});

export const GetStaleNeoTraceCleanupRunResponseSchema = z.object({
  run: CleanupRunDtoSchema,
  children: CleanupRunDtoSchema.array(),
});

export const TriggerStaleNeoTraceCleanupBodySchema = z.object({});

export const TriggerStaleNeoTraceApplicationCleanupBodySchema = z.object({
  applicationId: z.string().trim().min(1),
});

export const StaleNeoTraceCleanupStatusSchema = z.enum([
  'started',
  'blocked',
  'already_running',
  'partial',
  'failed',
]);

export const TriggerStaleNeoTraceCleanupResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  status: StaleNeoTraceCleanupStatusSchema,
  runId: z.string().optional(),
});

export type CleanupRunDto = z.infer<typeof CleanupRunDtoSchema>;
export type CleanupRunIssue = z.infer<typeof CleanupRunIssueSchema>;
export type CleanupRunScope = z.infer<typeof CleanupRunScopeSchema>;
export type CleanupRunStep = z.infer<typeof CleanupRunStepSchema>;
export type CleanupRunStatus = z.infer<typeof CleanupRunStatusSchema>;
export type GetStaleNeoTraceCleanupRunResponse = z.infer<
  typeof GetStaleNeoTraceCleanupRunResponseSchema
>;
export type ListStaleNeoTraceCleanupRunsResponse = z.infer<
  typeof ListStaleNeoTraceCleanupRunsResponseSchema
>;
export type StaleNeoTraceCleanupDashboard = z.infer<
  typeof StaleNeoTraceCleanupDashboardSchema
>;
